import http from '@root/utils/axios/admin'
import { getEcho } from '@/services/echo'

const getTurnFromFen = (fen) => {
    if (!fen) return 'w'
    return fen.split(' ')[1] || 'w'
}

const isSameMessage = (a, b) => {
    if (!a || !b) return false
    return String(a.id) === String(b.id)
}

const getMatchUuid = (match) => {
    return match?.uuid || match?.match_uuid || null
}

let state = {
    currentMatch: null,
    fen: null,
    isMyTurn: false,
    myColor: null,
    messages: [],
    waiting: false,
    waitingCount: 0,
    currentSubscription: null,
    queueInterval: null,
    gameEnded: false,
    winnerId: null,
    result: null,
    whiteTime: 600,
    blackTime: 600,
    timerInterval: null,
}

let actions = {
    joinQueue({ commit, dispatch }) {
        return new Promise((resolve, reject) => {
            http.post('/subscriber/matchmaking/join')
                .then(async (response) => {
                    commit('setWaiting', response.data.waiting || false)

                    if (response.data.matched && response.data.match_uuid) {
                        await dispatch('fetchMatch', response.data.match_uuid)
                        await dispatch('subscribeToMatch', response.data.match_uuid)
                    }

                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    leaveQueue({ commit }) {
        return new Promise((resolve, reject) => {
            http.post('/subscriber/matchmaking/leave')
                .then((response) => {
                    commit('setWaiting', false)
                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    checkQueueStatus() {
        return new Promise((resolve, reject) => {
            http.get('/subscriber/matchmaking/status')
                .then((response) => resolve(response.data))
                .catch(reject)
        })
    },

    fetchMatch({ commit, dispatch }, matchUuid) {
        return new Promise((resolve, reject) => {
            http.get(`/subscriber/matches/${matchUuid}`)
                .then((response) => {
                    commit('setMatchDetails', response.data)
                    dispatch('startRealtimeTimer')
                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    makeMove({ commit, state }, { matchUuid, matchId, from, to, promotion = null }) {
        return new Promise((resolve, reject) => {
            const uuid = matchUuid || matchId || getMatchUuid(state.currentMatch)

            if (!uuid) return reject(new Error('معرف المباراة غير موجود'))
            if (!state.isMyTurn) return reject(new Error('ليس دورك'))
            if (state.gameEnded) return reject(new Error('المباراة انتهت'))

            const payload = { from, to }
            if (promotion) payload.promotion = promotion

            commit('setIsMyTurn', false)

            http.post(`/subscriber/matches/${uuid}/move`, payload)
                .then((response) => {
                    if (response.data.fen) {
                        commit('setFen', response.data.fen)
                        const turn = getTurnFromFen(response.data.fen)
                        commit('setIsMyTurn', (turn === 'w' && state.myColor === 'white') || (turn === 'b' && state.myColor === 'black'))
                    }

                    if (response.data.white_time !== undefined) commit('setWhiteTime', response.data.white_time)
                    if (response.data.black_time !== undefined) commit('setBlackTime', response.data.black_time)

                    if (response.data.game_over) {
                        commit('setGameEnded', {
                            winnerId: response.data.winner_id || null,
                            result: response.data.result || null,
                        })
                    }

                    resolve(response.data)
                })
                .catch((error) => {
                    if (!state.gameEnded) {
                        const turn = getTurnFromFen(state.fen)
                        commit('setIsMyTurn', (turn === 'w' && state.myColor === 'white') || (turn === 'b' && state.myColor === 'black'))
                    }

                    reject(error)
                })
        })
    },

    sendMessage({ commit, state }, { matchUuid, matchId, message }) {
        return new Promise((resolve, reject) => {
            const uuid = matchUuid || matchId || getMatchUuid(state.currentMatch)
            if (!uuid) return reject(new Error('معرف المباراة غير موجود'))

            const cleanMessage = String(message || '').trim()
            if (!cleanMessage) return reject(new Error('الرسالة فارغة'))

            http.post(`/subscriber/matches/${uuid}/chat`, { message: cleanMessage })
                .then((response) => {
                    if (response.data.message) commit('pushMessage', response.data.message)
                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    fetchChatMessages({ commit, state }, matchUuid) {
        return new Promise((resolve, reject) => {
            const uuid = matchUuid || getMatchUuid(state.currentMatch)
            if (!uuid) return reject(new Error('معرف المباراة غير موجود'))

            http.get(`/subscriber/matches/${uuid}/chat`)
                .then((response) => {
                    commit('setMessages', response.data.messages || [])
                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    subscribeToMatch({ commit, state, dispatch }, matchUuid) {
        if (!matchUuid) return

        if (String(state.currentSubscription) === String(matchUuid)) return

        const echo = getEcho()

        if (!echo || typeof echo.private !== 'function') {
            console.warn('Echo is not ready')
            return
        }

        if (state.currentSubscription && String(state.currentSubscription) !== String(matchUuid)) {
            echo.leave(`match.${state.currentSubscription}`)
        }

        const channel = echo.private(`match.${matchUuid}`)

        channel.listen('.move.played', (data) => {
            if (data.fen) {
                commit('setFen', data.fen)
                const turn = getTurnFromFen(data.fen)
                commit('setIsMyTurn', (turn === 'w' && state.myColor === 'white') || (turn === 'b' && state.myColor === 'black'))
            }

            if (data.pgn !== undefined) commit('setPgn', data.pgn)
            if (data.white_time !== undefined) commit('setWhiteTime', data.white_time)
            if (data.black_time !== undefined) commit('setBlackTime', data.black_time)

            dispatch('startRealtimeTimer')
        })

        channel.listen('.game.ended', (data) => {
            if (state.gameEnded) return
            commit('setGameEnded', {
                winnerId: data.winner_id ? Number(data.winner_id) : null,
                result: data.result,
            })
        })

        channel.listen('.chat.message', (data) => commit('pushMessage', data))

        commit('setCurrentSubscription', matchUuid)
        dispatch('startRealtimeTimer')
    },

    startRealtimeTimer({ state, commit }) {
        if (state.timerInterval) clearInterval(state.timerInterval)

        const interval = setInterval(() => {
            if (state.gameEnded || !state.currentMatch) {
                clearInterval(interval)
                return
            }

            const turn = getTurnFromFen(state.fen)

            if (turn === 'w') {
                if (state.whiteTime > 0) commit('setWhiteTime', state.whiteTime - 1)
                else {
                    commit('setIsMyTurn', false)
                    clearInterval(interval)
                }
            }

            if (turn === 'b') {
                if (state.blackTime > 0) commit('setBlackTime', state.blackTime - 1)
                else {
                    commit('setIsMyTurn', false)
                    clearInterval(interval)
                }
            }
        }, 1000)

        commit('setTimerInterval', interval)
    },

    resign({ state }, matchUuid) {
        return new Promise((resolve, reject) => {
            const uuid = matchUuid || getMatchUuid(state.currentMatch)
            if (!uuid) return reject(new Error('معرف المباراة غير موجود'))

            http.post(`/subscriber/matches/${uuid}/resign`)
                .then((response) => resolve(response.data))
                .catch(reject)
        })
    },

    sendHeartbeat({ state }, matchUuid) {
        return new Promise((resolve, reject) => {
            const uuid = matchUuid || getMatchUuid(state.currentMatch)

            if (!uuid || state.gameEnded) return resolve({ success: true, skipped: true })

            http.post(`/subscriber/matches/${uuid}/heartbeat`)
                .then((response) => resolve(response.data))
                .catch(reject)
        })
    },

    startAgainstAI({ commit, dispatch }) {
        return new Promise((resolve, reject) => {
            dispatch('unsubscribeFromMatch')
            commit('clearMatch')

            http.post('/subscriber/matches/ai/start')
                .then(async (response) => {
                    await dispatch('fetchMatch', response.data.match_uuid)
                    await dispatch('fetchChatMessages', response.data.match_uuid)
                    await dispatch('subscribeToMatch', response.data.match_uuid)

                    resolve(response.data)
                })
                .catch(reject)
        })
    },

    getLeaderboard(context, payload = {}) {
        return new Promise((resolve, reject) => {
            http.get('/subscriber/leaderboard', { params: payload })
                .then((response) => resolve(response.data))
                .catch(reject)
        })
    },

    unsubscribeFromMatch({ commit, state }) {
        const uuid = state.currentSubscription

        const echo = getEcho()

        if (uuid && echo && typeof echo.leave === 'function') {
            echo.leave(`match.${uuid}`)
            commit('setCurrentSubscription', null)
        }

        if (state.timerInterval) {
            clearInterval(state.timerInterval)
            commit('setTimerInterval', null)
        }
    },

    clearMatchState({ commit, dispatch }) {
        dispatch('unsubscribeFromMatch')
        commit('clearMatch')
    },
    getMyGamePoints() {
    return new Promise((resolve, reject) => {
        http.get('/subscriber/game-points/me')
            .then((response) => {
                resolve(response.data)
            })
            .catch(reject)
    })
},
}
 
let mutations = {
    setWaiting(state, status) {
        state.waiting = status
    },

    setWaitingCount(state, count) {
        state.waitingCount = count
    },

    setQueueInterval(state, interval) {
        if (state.queueInterval) clearInterval(state.queueInterval)
        state.queueInterval = interval
    },

    setMatchDetails(state, data) {
        state.currentMatch = data.match
        state.fen = data.match?.fen || null
        state.myColor = data.my_color || null
        state.isMyTurn = Boolean(data.is_my_turn)
        state.whiteTime = Number(data.match?.white_time ?? 600)
        state.blackTime = Number(data.match?.black_time ?? 600)
        state.gameEnded = data.match?.status === 'finished'
        state.winnerId = data.match?.winner_id || null
        state.result = data.match?.result || null
    },

    setFen(state, fen) {
        state.fen = fen
        if (state.currentMatch) state.currentMatch.fen = fen
    },

    setPgn(state, pgn) {
        if (state.currentMatch) state.currentMatch.pgn = pgn
    },

    setIsMyTurn(state, value) {
        state.isMyTurn = Boolean(value)
    },

    setWhiteTime(state, value) {
        state.whiteTime = Math.max(0, Number(value || 0))
        if (state.currentMatch) state.currentMatch.white_time = state.whiteTime
    },

    setBlackTime(state, value) {
        state.blackTime = Math.max(0, Number(value || 0))
        if (state.currentMatch) state.currentMatch.black_time = state.blackTime
    },

    setTimerInterval(state, interval) {
        if (state.timerInterval && state.timerInterval !== interval) clearInterval(state.timerInterval)
        state.timerInterval = interval
    },

    setGameEnded(state, data) {
        state.gameEnded = true
        state.winnerId = data?.winnerId ?? null
        state.result = data?.result || null
        state.isMyTurn = false

        if (state.currentMatch) {
            state.currentMatch.status = 'finished'
            state.currentMatch.winner_id = state.winnerId
            state.currentMatch.result = state.result
        }

        if (state.timerInterval) {
            clearInterval(state.timerInterval)
            state.timerInterval = null
        }
    },

    pushMessage(state, message) {
        if (!message || !message.id) return

        const normalizedMessage = {
            ...message,
            user_id: message.user_id ?? message.subscriber_id ?? message.subscriber?.id ?? null,
            subscriber_id: message.subscriber_id ?? message.user_id ?? message.subscriber?.id ?? null,
        }

        if (!state.messages.some((m) => isSameMessage(m, normalizedMessage))) {
            state.messages.push(normalizedMessage)
        }
    },

    setMessages(state, messages) {
        state.messages = (messages || []).map((message) => ({
            ...message,
            user_id: message.user_id ?? message.subscriber_id ?? message.subscriber?.id ?? null,
            subscriber_id: message.subscriber_id ?? message.user_id ?? message.subscriber?.id ?? null,
        }))
    },

    setCurrentSubscription(state, matchUuid) {
        state.currentSubscription = matchUuid
    },

    clearMatch(state) {
        state.currentMatch = null
        state.fen = null
        state.isMyTurn = false
        state.myColor = null
        state.messages = []
        state.waiting = false
        state.waitingCount = 0
        state.gameEnded = false
        state.winnerId = null
        state.result = null
        state.whiteTime = 600
        state.blackTime = 600

        if (state.timerInterval) {
            clearInterval(state.timerInterval)
            state.timerInterval = null
        }
    },
}

export default {
    state,
    actions,
    mutations,
}
